import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useHabits, type Habit } from "@/context/SupabaseHabitContext";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import AddHabitForm from "./AddHabitForm";
import EditHabitForm from "./EditHabitForm";
import ReorderHabitsDialog from "./ReorderHabitsDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, ChevronDown, ChevronUp, Pause, Play, Pin, PinOff, Calendar, Heart, ArrowUpDown, Plus, Smartphone } from "lucide-react";
import HabitGrid from "./HabitGrid";
import UpgradeDialog from "./pricing/UpgradeDialog";

interface HabitTrackerProps {
  showPausedHabits?: boolean;
}

interface HabitCardProps {
  habit: Habit;
  isMobile: boolean;
  habitEditModes: Record<string, boolean>;
  onTogglePin: (id: string) => void;
  onTogglePause: (id: string, active: boolean) => void;
  onToggleEditMode: (id: string) => void;
  onEditHabit: (id: string) => void;
  onDeleteHabit: (id: string) => void;
  updateHabit: (id: string, updates: { active?: boolean; isCollapsed?: boolean }) => void;
}

function HabitCard({
  habit,
  isMobile,
  habitEditModes,
  onTogglePin,
  onTogglePause,
  onToggleEditMode,
  onEditHabit,
  onDeleteHabit,
  updateHabit,
}: HabitCardProps) {
  const navigate = useNavigate();
  return (
    <Card id={`habit-${habit.id}`} className="transition-all duration-300">
      <CardContent className="p-6">
        {!isMobile && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-medium">{habit.name}</h3>
              {habit.pinned && (
                <span title="Pinned habit">
                  <Pin className="h-3 w-3 text-primary" />
                </span>
              )}
              {habit.active === false && (
                <span className="text-xs text-muted-foreground">(Paused)</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => onTogglePause(habit.id, !habit.active)} title={habit.active === false ? "Resume habit" : "Pause habit"}>
                {habit.active === false ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onTogglePin(habit.id)} title={habit.pinned ? "Unpin habit" : "Pin habit"} className={habit.pinned ? "text-primary bg-primary/10 hover:bg-primary/20" : "hover:bg-accent"}>
                {habit.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => navigate("/widget-setup", { state: { habit } })} title="Get widget code">
                <Smartphone className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onToggleEditMode(habit.id)} title="Edit dates">
                <Calendar className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onEditHabit(habit.id)} title="Edit habit">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onDeleteHabit(habit.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => updateHabit(habit.id, { isCollapsed: !habit.isCollapsed })}>
                {habit.isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
        {isMobile && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-medium">{habit.name}</h3>
                {habit.pinned && <span title="Pinned habit"><Pin className="h-3 w-3 text-primary" /></span>}
                {habit.active === false && <span className="text-xs text-muted-foreground">(Paused)</span>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => updateHabit(habit.id, { isCollapsed: !habit.isCollapsed })}>
                {habit.isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </Button>
            </div>
            {!habit.isCollapsed && (
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Button variant="ghost" size="icon" onClick={() => onTogglePause(habit.id, !habit.active)} title={habit.active === false ? "Resume habit" : "Pause habit"} className="h-8 w-8">
                  {habit.active === false ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onTogglePin(habit.id)} title={habit.pinned ? "Unpin habit" : "Pin habit"} className={`h-8 w-8 ${habit.pinned ? "text-primary bg-primary/10 hover:bg-primary/20" : "hover:bg-accent"}`}>
                  {habit.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => navigate("/widget-setup", { state: { habit } })} title="Get widget code" className="h-8 w-8">
                  <Smartphone className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onToggleEditMode(habit.id)} title="Edit dates" className="h-8 w-8">
                  <Calendar className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onEditHabit(habit.id)} title="Edit habit" className="h-8 w-8">
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onDeleteHabit(habit.id)} title="Delete habit" className="h-8 w-8">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        )}
        {!habit.isCollapsed && (
          <div className={`mt-4 ${habit.active === false ? "opacity-50" : ""}`}>
            <HabitGrid habit={habit} initialMonths={isMobile ? 4 : 8} externalEditMode={habitEditModes[habit.id] || false} onToggleEditMode={() => onToggleEditMode(habit.id)} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function HabitTracker({ showPausedHabits = false }: HabitTrackerProps) {
  const { habits, loading, deleteHabit, reorderHabits, updateHabit, toggleHabitPin, maxFreeHabits, activeHabitsCount, canAddMoreHabits } = useHabits();
  const { isPremium } = useAuth();
  const isMobile = useIsMobile();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingHabit, setEditingHabit] = useState<string | null>(null);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [habitEditModes, setHabitEditModes] = useState<Record<string, boolean>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [habitToDelete, setHabitToDelete] = useState<string | null>(null);
  
  // State for mood tracker visibility - connected to the combined component
  const [showMoodTracker, setShowMoodTracker] = useState<boolean>(() => {
    const saved = localStorage.getItem('showMoodTracker');
    return saved ? JSON.parse(saved) : false; // Default to false (hide mood tracker)
  });

  // Ref to track current state for immediate access
  const showMoodTrackerRef = useRef(showMoodTracker);

  // Save to localStorage whenever showMoodTracker changes
  useEffect(() => {
    localStorage.setItem('showMoodTracker', JSON.stringify(showMoodTracker));
    showMoodTrackerRef.current = showMoodTracker;
  }, [showMoodTracker]);

  // Listen for hide button clicks from MoodTrackerWithAnalysis
  useEffect(() => {
    const handleMoodTrackerHide = (event: CustomEvent) => {
      const newValue = event.detail.showMoodTracker;
      // Only update if the value is actually different
      if (showMoodTrackerRef.current !== newValue) {
        // Small delay to prevent rapid state changes
        setTimeout(() => {
          setShowMoodTracker(newValue);
          showMoodTrackerRef.current = newValue;
        }, 50);
      }
    };

    window.addEventListener('moodTrackerHide', handleMoodTrackerHide as EventListener);

    return () => {
      window.removeEventListener('moodTrackerHide', handleMoodTrackerHide as EventListener);
    };
  }, []);

  // Function to toggle mood tracker and notify other components immediately
  const toggleMoodTracker = () => {
    const currentValue = showMoodTrackerRef.current;
    const newValue = !currentValue;
    
    setShowMoodTracker(newValue);
    showMoodTrackerRef.current = newValue;
    
    // Dispatch custom event for immediate synchronization
    const event = new CustomEvent('moodTrackerToggle', {
      detail: { showMoodTracker: newValue }
    });
    window.dispatchEvent(event);
  };

  const handleAddHabitClick = () => {
    if (!isPremium && !canAddMoreHabits) {
      setUpgradeDialogOpen(true);
    } else {
      setAddDialogOpen(true);
    }
  };

  const toggleHabitEditMode = (habitId: string) => {
    setHabitEditModes(prev => ({
      ...prev,
      [habitId]: !prev[habitId]
    }));
  };

  const handleDeleteHabit = (habitId: string) => {
    setHabitToDelete(habitId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteHabit = () => {
    if (habitToDelete) {
      deleteHabit(habitToDelete);
      setHabitToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const cancelDeleteHabit = () => {
    setHabitToDelete(null);
    setDeleteConfirmOpen(false);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-6 bg-muted rounded w-1/4"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isPremium && (
        <div className="text-sm text-muted-foreground flex items-center justify-between bg-muted/50 rounded-lg p-3">
          <span>Free Plan</span>
          <span>
            Habits: {activeHabitsCount}/{maxFreeHabits}
          </span>
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Your Habits</h2>
        <div className={`flex items-center gap-2 ${isMobile ? 'flex-row' : ''}`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleMoodTracker}
            className={`flex items-center gap-1 transition-all duration-200 ${isMobile ? 'px-2' : ''} ${
              showMoodTracker 
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 border border-primary' 
                : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
            }`}
            title="Mood Tracker"
          >
            <Heart className={`h-4 w-4 transition-all duration-200 ${showMoodTracker ? 'fill-current' : ''}`} />
            {showMoodTracker ? 'Hide Mood' : 'Mood Tracker'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddHabitClick}
            className={isMobile ? 'px-2' : ''}
            title="Add Habit"
          >
            {isMobile ? (
              <Plus className="h-4 w-4" />
            ) : (
              'Add Habit'
            )}
          </Button>
          {habits.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReorderDialogOpen(true)}
              className={isMobile ? 'px-2' : ''}
              title="Reorder Habits"
            >
              {isMobile ? (
                <ArrowUpDown className="h-4 w-4" />
              ) : (
                'Reorder Habits'
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {habits.filter(habit => showPausedHabits || habit.active !== false).map((habit) => (
          <HabitCard
            key={habit.id}
            habit={habit}
            isMobile={isMobile}
            habitEditModes={habitEditModes}
            onTogglePin={toggleHabitPin}
            onTogglePause={(id, active) => updateHabit(id, { active })}
            onToggleEditMode={toggleHabitEditMode}
            onEditHabit={(id) => { setEditingHabit(id); setEditDialogOpen(true); }}
            onDeleteHabit={handleDeleteHabit}
            updateHabit={updateHabit}
          />
        ))}


      </div>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a new habit</DialogTitle>
          </DialogHeader>
          <AddHabitForm 
            onClose={() => setAddDialogOpen(false)}
            onUpgradeRequired={() => setUpgradeDialogOpen(true)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit habit</DialogTitle>
          </DialogHeader>
          {editingHabit && (
            <EditHabitForm
              habit={habits.find(h => h.id === editingHabit)!}
              onClose={() => setEditDialogOpen(false)}
              onUpgradeRequired={() => setUpgradeDialogOpen(true)}
            />
          )}
        </DialogContent>
      </Dialog>

      <ReorderHabitsDialog
        open={reorderDialogOpen}
        onOpenChange={setReorderDialogOpen}
        habits={habits}
        onReorder={reorderHabits}
      />

      <UpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
      />

             <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
         <AlertDialogContent>
           <AlertDialogHeader>
             <AlertDialogTitle>Are you sure you want to delete this habit?</AlertDialogTitle>
             <AlertDialogDescription>
               This action cannot be undone. All progress data for "{habitToDelete ? habits.find(h => h.id === habitToDelete)?.name : ''}" will be permanently deleted.
             </AlertDialogDescription>
           </AlertDialogHeader>
           <AlertDialogFooter>
             <AlertDialogCancel onClick={cancelDeleteHabit}>Cancel</AlertDialogCancel>
             <AlertDialogAction 
               onClick={confirmDeleteHabit}
               className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
             >
               Delete
             </AlertDialogAction>
           </AlertDialogFooter>
         </AlertDialogContent>
       </AlertDialog>
    </div>
  );
}
